import React from 'react'
import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { ArcGauge, tierStyle, type PerformanceTier } from './ArcGauge'
import { formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'

export interface MonthBand {
  month: string
  monthLabel: string
  monthShort: string
  year: number
  otb: number
  stly: number
  lyEnd: number
  stlyDeltaPct: number | null
  lyEndDeltaPct: number | null
  performancePct: number | null
  performanceTier: PerformanceTier
  performanceLabel: string | null
  occupancyOtbPct: number
  occupancyStlyPct: number
  occupancyDeltaPct: number | null
  hasStlyData: boolean
  hasLyEndData: boolean
}

interface MonthCardProps {
  band: MonthBand
  currency: string
  /** When true, render a more compact card (used in dense lists). */
  compact?: boolean
}

function DeltaPill({ pct, neutralLabel }: { pct: number | null; neutralLabel: string }) {
  if (pct == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-400">
        <Minus className="h-3 w-3" /> {neutralLabel}
      </span>
    )
  }
  const positive = pct >= 0
  const Icon = positive ? ArrowUp : ArrowDown
  const cls = positive ? 'text-emerald-600' : 'text-rose-600'
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums', cls)}>
      <Icon className="h-3 w-3" />
      {Math.abs(pct).toFixed(1)}%
    </span>
  )
}

export function MonthCard({ band, currency }: MonthCardProps) {
  const style = tierStyle(band.performanceTier)
  const hasComp = band.hasStlyData

  return (
    <article
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm transition-all duration-300',
        'hover:-translate-y-0.5 hover:shadow-md hover:border-border'
      )}
    >
      {/* Tier accent stripe */}
      <div
        className={cn(
          'h-1 w-full',
          band.performanceTier === 'excellent' && 'bg-emerald-500',
          band.performanceTier === 'good' && 'bg-teal-600',
          band.performanceTier === 'average' && 'bg-amber-500',
          band.performanceTier === 'bad' && 'bg-rose-500',
          !band.performanceTier && 'bg-slate-200'
        )}
      />

      <div className="p-5">
        {/* Header */}
        <div className="mb-4 flex items-baseline justify-between">
          <div>
            <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
              {band.monthLabel}
            </h3>
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {band.year}
            </p>
          </div>
          {band.performanceLabel && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                style.bg,
                style.text,
                style.ring
              )}
            >
              {band.performanceLabel}
            </span>
          )}
        </div>

        {/* Arc + summary */}
        <div className="flex items-center gap-5">
          <div className="shrink-0">
            <ArcGauge pct={band.performancePct} tier={band.performanceTier} size={120} />
          </div>

          <div className="min-w-0 flex-1 space-y-3">
            <MetricRow
              label="OTB"
              tone="primary"
              value={formatMoney(band.otb, currency)}
              delta={hasComp ? <DeltaPill pct={band.stlyDeltaPct} neutralLabel="vs STLY" /> : null}
            />
            <MetricRow
              label="STLY"
              tone="muted"
              value={band.hasStlyData ? formatMoney(band.stly, currency) : '—'}
              hint="364 days ago"
            />
            {/* "LY final" is the locked compact label for lyEnd — docs/revenue-vocabulary.md §5.
                The JSON key stays lyEnd; only the operator-facing word is canonicalised. */}
            <MetricRow
              label="LY final"
              tone="muted"
              value={band.hasLyEndData ? formatMoney(band.lyEnd, currency) : '—'}
              delta={band.hasLyEndData ? <DeltaPill pct={band.lyEndDeltaPct} neutralLabel="vs LY final" /> : null}
            />
          </div>
        </div>

        {/* Occupancy strip */}
        <div className="mt-5 border-t border-dashed border-border/60 pt-3">
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Occupancy
            </span>
            <span className="tabular-nums text-muted-foreground">
              <span className="font-semibold text-foreground">{band.occupancyOtbPct.toFixed(1)}%</span>
              <span className="mx-1.5 text-slate-300">·</span>
              <span>STLY {band.occupancyStlyPct.toFixed(1)}%</span>
            </span>
          </div>
          <OccupancyBar otb={band.occupancyOtbPct} stly={band.occupancyStlyPct} />
        </div>
      </div>
    </article>
  )
}

function MetricRow({
  label,
  value,
  tone,
  delta,
  hint,
}: {
  label: string
  value: string
  tone: 'primary' | 'muted'
  delta?: React.ReactNode
  hint?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </span>
        {hint && <span className="text-[9px] italic text-slate-400">{hint}</span>}
      </div>
      <div className="flex items-baseline gap-2">
        {delta}
        <span
          className={cn(
            'tabular-nums',
            tone === 'primary'
              ? 'font-display text-base font-semibold text-foreground'
              : 'text-sm font-medium text-muted-foreground'
          )}
        >
          {value}
        </span>
      </div>
    </div>
  )
}

function OccupancyBar({ otb, stly }: { otb: number; stly: number }) {
  const otbWidth = Math.max(0, Math.min(100, otb))
  const stlyPos = Math.max(0, Math.min(100, stly))
  return (
    <div className="relative mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-teal-500 to-teal-600 transition-[width] duration-700 ease-out"
        style={{ width: `${otbWidth}%` }}
      />
      {/* STLY marker tick */}
      <div
        className="absolute inset-y-[-1px] w-px bg-slate-500/70"
        style={{ left: `${stlyPos}%` }}
        aria-hidden
      />
    </div>
  )
}
